import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getReviews, createReview } from "../../lib/api";
import { BRAND } from "../../lib/brand";
import { Star, Quote, Loader2, ExternalLink } from "lucide-react";

const Stars = ({ n, size = 16, onSet }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={onSet ? () => onSet(i) : undefined}
        data-testid={onSet ? `star-${i}` : undefined}
        className={onSet ? "press" : "cursor-default"}
        aria-label={`${i} star`}
      >
        <Star size={size} fill={i <= n ? "#7CB518" : "none"} stroke={i <= n ? "#7CB518" : "#555"} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ onPosted }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => getReviews().then((r) => { setReviews(r); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Add your name and a review."); return; }
    setSubmitting(true);
    try {
      await createReview(form);
      toast.success("Thanks for the review!");
      setForm({ name: "", location: "", rating: 5, text: "" });
      load();
      onPosted && onPosted();
    } catch {
      toast.error("Couldn't post review. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="bg-[#111] section-divider">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#7CB518]">// Straight From The Curb</span>
            <h2 className="font-display uppercase text-4xl md:text-6xl mt-2 leading-none">
              Customer <span className="text-[#7CB518]">Reviews</span>
            </h2>
          </div>
          <a
            href={BRAND.googleReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-btn"
            className="press inline-flex items-center gap-2 self-start md:self-auto bg-transparent text-white font-display uppercase text-sm px-6 py-3 border-2 border-[#7CB518] hard-shadow shrink-0"
          >
            <Star size={16} fill="#7CB518" stroke="#7CB518" /> Review Us On Google <ExternalLink size={15} />
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {loading ? (
              <div className="h-40 grid place-items-center"><Loader2 className="animate-spin text-[#7CB518]" /></div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {reviews.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                    data-testid={`review-card-${i}`}
                    className="border-2 border-[#2d2d2d] bg-[#0a0a0a] p-6 hover:border-[#7CB518] transition-colors"
                  >
                    <Quote className="text-[#7CB518] mb-3" size={22} />
                    <p className="text-sm text-white/85 leading-relaxed">"{r.text}"</p>
                    <div className="mt-4 pt-4 border-t-2 border-[#2d2d2d] flex items-center justify-between">
                      <div>
                        <div className="font-bold text-sm">{r.name}</div>
                        {r.location && <div className="text-xs text-white/40 uppercase tracking-widest">{r.location}</div>}
                      </div>
                      <Stars n={r.rating} />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Submit form */}
          <form onSubmit={submit} data-testid="review-form" className="border-2 border-[#7CB518] bg-[#0a0a0a] p-6 h-fit space-y-4 lg:sticky lg:top-24">
            <h3 className="font-display uppercase text-2xl">Leave A Review</h3>
            <div>
              <span className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Your Rating</span>
              <Stars n={form.rating} size={28} onSet={(v) => setForm({ ...form, rating: v })} />
            </div>
            <input data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="brutal-input" placeholder="Your name" />
            <input data-testid="review-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="brutal-input" placeholder="Town (optional)" />
            <textarea data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={4} className="brutal-input resize-none" placeholder="How'd we do?" />
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-review-btn"
              className="press w-full bg-[#7CB518] text-black font-display uppercase py-3 border-2 border-[#7CB518] hard-shadow-white disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : "Post Review"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
